/**
 * Thin fetch wrapper for talking to our own backend.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.status = status;
    this.body = body;
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, body, `${init?.method ?? "GET"} ${path} -> ${res.status}`);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function apiErrorCode(err: ApiError): string | undefined {
  const body = err.body as { error?: string } | null;
  return typeof body?.error === "string" ? body.error : undefined;
}

export function apiErrorMessage(err: ApiError): string | undefined {
  const body = err.body as { message?: string; stripe?: { type?: string; code?: string } } | null;
  if (typeof body?.message === "string" && body.message.trim()) return body.message.trim();
  if (body?.stripe?.code) return `${body.stripe.type ?? "stripe_error"}: ${body.stripe.code}`;
  return undefined;
}

export function formatMoney(
  cents: number,
  locale = "pt-BR",
  currency: "BRL" | "USD" = locale === "en-US" || locale === "es-ES" ? "USD" : "BRL",
): string {
  if (currency === "USD") {
    const amount = (cents / 100).toLocaleString(locale, {
      minimumFractionDigits: Number.isInteger(cents / 100) ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `US$ ${amount}`;
  }
  return (cents / 100).toLocaleString(locale, {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** @deprecated Prefer formatMoney(cents, locale). */
export function formatBRL(cents: number, locale = "pt-BR"): string {
  return formatMoney(cents, locale);
}
